// @ts-nocheck
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

class PenBundlePreview extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  #syncingGifts = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      this.data = JSON.parse(this.querySelector('script[data-pen-bundle-preview-state]')?.textContent || '{}');
    } catch {
      this.data = {};
    }

    this.productId = Number(this.data.productId);
    this.activeVariantId = Number(this.data.activeVariantId);
    this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
    this.cartVariantIds = [];
    this.selectedCount = Number(this.dataset.defaultCount || 2) || 2;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'USD';

    this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
    this.addButtons = Array.from(this.querySelectorAll('[data-bundle-add]'));
    this.addButtons.forEach((button) => {
      button.addEventListener('click', this.#handleAddClick, { signal });
    });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#render();
    this.#refreshCart();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onVariantUpdate = (event) => {
    const productId = Number(event.detail?.data?.productId);
    if (productId && productId !== this.productId) return;

    const variantId = Number(event.detail?.resource?.id);
    if (!variantId) return;

    this.activeVariantId = variantId;
    this.#render();
  };

  #onCartUpdate = (event) => {
    const items = event.detail?.resource?.items;
    if (Array.isArray(items)) {
      this.#setCartItems(items);
      this.#syncGifts(items);
      return;
    }
    const delay = event.detail?.data?.source === 'product-form-component' ? 450 : 120;
    window.setTimeout(() => this.#refreshCart(), delay);
  };

  #handleAddClick = (event) => {
    const button = /** @type {HTMLElement | undefined} */ (event.currentTarget);
    const card = button?.closest?.('[data-bundle-card]');
    if (!card || !(card instanceof HTMLElement)) return;

    if (!card.classList.contains('is-unlocked')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    event.preventDefault();
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer instanceof HTMLElement && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
  };

  async #refreshCart() {
    try {
      const cart = await this.#fetchCart();
      if (cart?.items) {
        this.#setCartItems(cart.items || []);
        await this.#syncGifts(cart.items || []);
      }
    } catch {
      // Visual preview remains useful from the active variant if cart cannot be read.
    }
  }

  async #fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) return null;
    return response.json();
  }

  #setCartItems(items) {
    const ids = [];
    for (const item of items) {
      if (Number(item.product_id) !== this.productId || this.#isBundleGift(item)) continue;
      const quantity = Number(item.quantity || 1);
      for (let i = 0; i < quantity; i += 1) ids.push(Number(item.variant_id));
    }
    this.cartVariantIds = ids.filter(Boolean);
    this.#render();
  }

  #variantById(id) {
    return this.variants.find((variant) => Number(variant.id) === Number(id)) || null;
  }

  #giftVariantIds() {
    const twoCard = this.cards.find((card) => Number(card.dataset.bundleCount || 0) === 2);
    const threeCard = this.cards.find((card) => Number(card.dataset.bundleCount || 0) === 3);
    return {
      giftOne: Number(twoCard?.dataset.giftOneVariantId || threeCard?.dataset.giftOneVariantId || 0),
      giftTwo: Number(threeCard?.dataset.giftTwoVariantId || 0),
    };
  }

  #isBundleGift(item) {
    const props = item?.properties || {};
    return props._bundle_gift === true || props._bundle_gift === 'true';
  }

  #sectionIds() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    return sectionIds;
  }

  async #syncGifts(items) {
    if (this.#syncingGifts) return;

    const { giftOne, giftTwo } = this.#giftVariantIds();
    if (!giftOne && !giftTwo) return;

    const penQty = items.reduce((total, item) => {
      if (Number(item.product_id) !== this.productId || this.#isBundleGift(item)) return total;
      return total + Number(item.quantity || 0);
    }, 0);
    const desired = new Set();
    if (penQty >= 2 && giftOne) desired.add(giftOne);
    if (penQty >= 3 && giftTwo) desired.add(giftTwo);

    const managedGiftIds = new Set([giftOne, giftTwo].filter(Boolean));
    const bundleGiftItems = items.filter(
      (item) => this.#isBundleGift(item) && managedGiftIds.has(Number(item.variant_id))
    );
    const present = new Map();
    bundleGiftItems.forEach((item) => {
      const id = Number(item.variant_id);
      if (!present.has(id)) present.set(id, []);
      present.get(id).push(item);
    });

    const toAdd = [...desired].filter((id) => !present.has(id));
    const toChange = bundleGiftItems
      .map((item) => {
        const variantId = Number(item.variant_id);
        const shouldKeep = desired.has(variantId) && present.get(variantId)?.[0] === item;
        if (!shouldKeep) return { key: item.key, quantity: 0 };
        if (Number(item.quantity || 1) !== 1) return { key: item.key, quantity: 1 };
        return null;
      })
      .filter(Boolean);

    if (!toAdd.length && !toChange.length) return;

    this.#syncingGifts = true;
    const sectionIds = this.#sectionIds();
    let sections;

    try {
      for (const item of toChange) {
        const response = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', {
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          })
        );
        const data = await response.json();
        if (data.sections) sections = data.sections;
      }

      if (toAdd.length) {
        const response = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items: toAdd.map((id) => ({
                id,
                quantity: 1,
                properties: {
                  _bundle_gift: 'true',
                  _bundle_tier: penQty >= 3 ? '3_pens' : '2_pens',
                },
              })),
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          })
        );
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message || 'Gift add failed');
        if (data.sections) sections = data.sections;
      }

      const cart = await this.#fetchCart();
      if (cart?.items) this.#setCartItems(cart.items);
      document.dispatchEvent(
        new CartUpdateEvent(cart || {}, 'pen-bundle-preview-gifts', {
          source: 'pen-bundle-preview-gifts',
          productId: String(this.productId),
          itemCount: penQty,
          sections,
        })
      );
    } catch (error) {
      console.error('Pen bundle preview: gift sync failed', error);
    } finally {
      this.#syncingGifts = false;
    }
  }

  #variantIdsForCount(count) {
    const ids = [...this.cartVariantIds].filter(Boolean);
    while (ids.length < count) ids.push(this.activeVariantId);
    return ids.slice(0, count);
  }

  #render() {
    const activeVariant = this.#variantById(this.activeVariantId);
    const bundleCount = this.cartVariantIds.length;

    this.cards.forEach((card) => {
      const count = Number(card.dataset.bundleCount || 1) || 1;
      const slots = Array.from(card.querySelectorAll('[data-pen-slot]'));
      const ids = this.#variantIdsForCount(count);
      const unlocked = count >= 3 ? bundleCount >= 3 : bundleCount === 2;

      card.classList.toggle('is-unlocked', unlocked);
      card.classList.toggle('is-locked', !unlocked);

      slots.forEach((slot, index) => {
        const variant = this.#variantById(ids[index]) || activeVariant;
        slot.innerHTML = variant ? this.#renderPen(variant) : '';
        slot.toggleAttribute('data-is-active-pen', index === 0);
      });

      this.#renderPrices(card, activeVariant);
      this.#syncUnlockButtonA11y(card, unlocked);
    });
  }

  /** @param {Element} card @param {boolean} unlocked */
  #syncUnlockButtonA11y(card, unlocked) {
    const btn = card.querySelector('[data-bundle-add]');
    if (!(btn instanceof HTMLElement)) return;
    const openCartAria = (this.dataset.openCartLabel || '').trim();
    if (unlocked && openCartAria) btn.setAttribute('aria-label', openCartAria);
    else btn.removeAttribute('aria-label');
  }

  #renderPrices(card, activeVariant) {
    if (!activeVariant || typeof activeVariant.price !== 'number') return;

    const count = Number(card.dataset.bundleCount || 1) || 1;
    if (count <= 0) return;

    const discount = Number(card.dataset.bundleDiscount || 0) || 0;
    const ids = this.#variantIdsForCount(count);

    let pensCompareTotal = 0;
    for (const id of ids) {
      const v = this.#variantById(id);
      if (v && typeof v.price === 'number') pensCompareTotal += v.price;
    }

    const pensSaleTotal = Math.round((pensCompareTotal * (100 - discount)) / 100);
    const perPenCompare = Math.round(pensCompareTotal / count);
    const perPenSale = Math.round(pensSaleTotal / count);

    const compareEl = card.querySelector('[data-bundle-compare]');
    const priceEl = card.querySelector('[data-bundle-price]');

    if (compareEl) {
      compareEl.textContent = discount > 0 ? formatMoney(perPenCompare, this.moneyFormat, this.currency) : '';
      compareEl.hidden = discount <= 0;
    }

    if (priceEl) {
      priceEl.textContent = formatMoney(perPenSale, this.moneyFormat, this.currency);
    }
  }

  #renderPen(variant) {
    const body = this.#escapeAttr(variant.body || '');
    const cap = this.#escapeAttr(variant.cap || '');
    const alt = this.#escapeAttr(variant.title || variant.options?.join(' / ') || 'Selected pen');

    return `
      <span class="pen-bundle-card__pen-stage" aria-label="${alt}">
        ${body ? `<img class="pen-bundle-card__pen-layer pen-bundle-card__pen-layer--body" data-layer="body" alt="" src="${body}" loading="lazy">` : ''}
        ${cap ? `<img class="pen-bundle-card__pen-layer pen-bundle-card__pen-layer--cap" data-layer="cap" alt="" src="${cap}" loading="lazy">` : ''}
      </span>
    `;
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #escapeAttr(value) {
    return this.#escapeHtml(value);
  }
}

if (!customElements.get('pen-bundle-preview')) {
  customElements.define('pen-bundle-preview', PenBundlePreview);
}
